/**
 * Matching uploaded headshot files to students by filename.
 *
 * Headshots arrive as a folder of images mailed to info@, named by whoever took them. The
 * documented convention is `First_Last.jpg`, but the matcher is deliberately tolerant of how
 * people actually name files — separators vary, name order varies, and diacritics survive or
 * do not depending on the phone.
 *
 * Nothing here decides anything on its own: every match is shown to an admin for confirmation
 * before an upload happens. Being generous is therefore cheap, and being strict would just
 * mean more files an admin has to pair by hand.
 */

/** A file whose name is a bare student UUID matches that student outright. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Trailing OS/browser dedupe markers: `photo (1).jpg`, `photo copy 2.jpg`. */
const DEDUPE_SUFFIX_RE = /(?:\s*\(\d+\)|\s+copy(?:\s+\d+)?)$/i;

export interface MatchableStudent {
  id: string;
  first_name: string;
  last_name: string;
}

export type PhotoNameMatch =
  | { kind: 'matched'; studentId: string; fuzzy: boolean }
  | { kind: 'ambiguous'; studentIds: string[] }
  | { kind: 'unmatched' };

/** Strip the extension from a filename, leaving the stem. */
export function fileStem(filename: string): string {
  const cut = filename.lastIndexOf('.');
  return cut > 0 ? filename.slice(0, cut) : filename;
}

/**
 * Reduce a name to comparable word tokens: accent-folded, punctuation-free, lowercase.
 *
 * `"Núñez-García, Ana Sofía"` and `"ana_sofia_nunez_garcia"` both reduce to the same four
 * tokens, which is the whole point — those are the same child photographed twice.
 */
export function nameTokens(raw: string): string[] {
  return raw
    .normalize('NFD')
    // Drop the combining accents NFD split off. Must happen before the punctuation pass
    // below, which would otherwise turn each one into a space and split "Núñez" in two.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Order-independent identity for a set of name tokens.
 *
 * Sorting is what lets `First_Last.jpg` and `Last, First.jpg` land on the same student
 * without the admin having to know which convention the photographer used. The cost is that
 * two students whose names are anagrams of each other collide — that is reported as
 * `ambiguous` rather than guessed at.
 */
export function nameKey(raw: string): string {
  return nameTokens(raw).sort().join(' ');
}

/** The filename stem, with dedupe markers removed. Exported for the review UI's labels. */
export function cleanedStem(filename: string): string {
  return fileStem(filename).replace(DEDUPE_SUFFIX_RE, '').trim();
}

/**
 * Pair one file with a student.
 *
 * Three passes, narrowing only when the previous found nothing:
 *   1. bare UUID filename — the escape hatch for a file an admin has already paired by hand
 *   2. exact token-set match
 *   3. superset match — the file carries a middle name the roster does not, or vice versa
 *
 * Every pass reports multiple hits as `ambiguous` instead of picking one. Handing a child to
 * the wrong adult is the failure this feature exists to prevent, so a headshot attached to
 * the wrong student is worse than no headshot at all.
 */
export function matchFilenameToStudents(
  filename: string,
  students: readonly MatchableStudent[],
): PhotoNameMatch {
  const stem = cleanedStem(filename);

  if (UUID_RE.test(stem)) {
    const byId = students.find(s => s.id.toLowerCase() === stem.toLowerCase());
    return byId ? { kind: 'matched', studentId: byId.id, fuzzy: false } : { kind: 'unmatched' };
  }

  const fileTokens = nameTokens(stem);
  if (fileTokens.length === 0) return { kind: 'unmatched' };
  const fileKey = fileTokens.slice().sort().join(' ');

  const exact = students.filter(s => nameKey(`${s.first_name} ${s.last_name}`) === fileKey);
  if (exact.length === 1) return { kind: 'matched', studentId: exact[0].id, fuzzy: false };
  if (exact.length > 1) return { kind: 'ambiguous', studentIds: exact.map(s => s.id) };

  // Both of the student's own name parts appear in the filename, in any order and among any
  // number of extras. Requires each part to be a whole token: "Li" must not match "Lily".
  const fileTokenSet = new Set(fileTokens);
  const superset = students.filter(s => {
    const parts = [...nameTokens(s.first_name), ...nameTokens(s.last_name)];
    return parts.length > 0 && parts.every(part => fileTokenSet.has(part));
  });
  if (superset.length === 1) return { kind: 'matched', studentId: superset[0].id, fuzzy: true };
  if (superset.length > 1) return { kind: 'ambiguous', studentIds: superset.map(s => s.id) };

  return { kind: 'unmatched' };
}
